import React, {
    useState, useEffect, useRef, useMemo, useCallback
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Text, Box, Divider, Grid
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import {
    renderTopCell,
    renderPriceCell,
    renderRatingCell,
    renderAttributeCell
} from 'components/ProductPage/ProductCompareTool/ProductCompareToolItem/ProductCompareToolItem';
import { colors, site, space } from 'style/config';
import ProductCompareToolEN from 'components/ProductPage/ProductCompareTool/locales/ProductCompareTool_en_US';
import ProductCompareToolFR from 'components/ProductPage/ProductCompareTool/locales/ProductCompareTool_fr_CA';
import pdpCompareUtils from 'utils/pdpCompare';
import uiUtils from 'utils/UI';

const { SKELETON_TEXT } = uiUtils;
const SKELETON_COLUMN_COUNT = 6;
const SKELETON_SKUS = Array.from({ length: SKELETON_COLUMN_COUNT }, (_, i) => ({ skuId: `skeleton-${i}` }));

// Height at which collapsible attribute rows are clipped when collapsed
const COLLAPSED_MAX_HEIGHT = 50;
// Height of the gradient fade overlay at the bottom of the collapsed section
const GRADIENT_OVERLAY_HEIGHT = 50;

const ProductCompareTool = props => {
    const {
        podId,
        skuList,
        title,
        resultId,
        totalResults,
        isCarousel,
        similarProductVariants,
        isLoading,
        pdpCompareAttributes,
        isCollapsible,
        currentProduct,
        hideHeader
    } = props;

    const getText = localeUtils.getLocalisedDataFromResource(ProductCompareToolEN, ProductCompareToolFR);

    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);
    const scrollRef = useRef(null);

    const transformedProduct = useMemo(() => pdpCompareUtils.transformProductDataToRecsServiceFormat(currentProduct), [currentProduct]);

    const recommendedSkus = useMemo(() => {
        if (podId && skuList && skuList.length > 0) {
            const skusToUse = skuList.length === 6 ? skuList.slice(0, 5) : skuList;

            return [transformedProduct, ...skusToUse];
        }

        return [];
    }, [podId, skuList, transformedProduct]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = 0;
        }
    }, [podId, skuList, transformedProduct]);

    // Filter pdpCompareAttributes to only include those present in some of the sku facets or 'price' and 'rating'
    const filteredPdpCompareAttributes = useMemo(() => {
        const facetNames = new Set();

        // Include facets from the transformed product (first column)
        transformedProduct?.facets?.forEach(facet => {
            if (facet.name) {
                facetNames.add(facet.name);
            }
        });

        // Include facets from skuList
        skuList?.forEach(sku => {
            sku?.facets?.forEach(facet => {
                if (facet.name) {
                    facetNames.add(facet.name);
                }
            });
        });

        const attributesToFilter = pdpCompareAttributes?.length > 0 ? pdpCompareAttributes : ['price', 'rating'];

        const filtered = attributesToFilter.filter(attribute => {
            //Attribute is static
            if (attribute === 'price' || attribute === 'rating') {
                return true;
            }

            //Attribute is dynamic string, check if present in facet names
            if (typeof attribute === 'string') {
                return facetNames.has(attribute);
            }

            //Attribute is dynamic object extract values string
            if (typeof attribute === 'object' && attribute !== null) {
                const attributeValues = Object.values(attribute)?.[0] || [];

                return attributeValues.some(attValue => facetNames.has(attValue));
            }

            return false;
        });

        return filtered;
    }, [pdpCompareAttributes, skuList, transformedProduct]);

    const hasCollapsibleAttributes = useMemo(
        () => filteredPdpCompareAttributes.some(attr => attr !== 'price' && attr !== 'rating'),
        [filteredPdpCompareAttributes]
    );

    // Calculate grid config: always render 6 columns to fill container width
    const gridConfig = useMemo(() => {
        const skus = isLoading ? SKELETON_SKUS : recommendedSkus;
        const usedColumnCount = Math.min(skus?.length || 0, 6);
        const columnWidth = site.containerMax / 6;
        const widthPx = columnWidth * (usedColumnCount || 1);

        return {
            columnCount: usedColumnCount,
            columns: `repeat(${usedColumnCount || 1}, ${columnWidth}px)`,
            widthPx
        };
    }, [recommendedSkus?.length, isLoading]);

    return (
        <Box
            lineHeight='tight'
            marginBottom={hideHeader ? 0 : [6, 7]}
        >
            {!hideHeader && <Divider />}
            {!hideHeader && (
                <Text
                    is='h2'
                    marginY={4}
                    fontSize={['md', 'lg']}
                    fontWeight='bold'
                    css={isLoading && [{ minWidth: 204 }, SKELETON_TEXT]}
                    children={title}
                />
            )}
            {similarProductVariants}
            {isLoading ? (
                <Grid
                    columns={gridConfig.columns}
                    fontSize={['sm', 'base']}
                    gap={null}
                    css={{
                        minWidth: gridConfig.columnCount < 6 ? `${gridConfig.widthPx}px` : '100%'
                    }}
                >
                    {SKELETON_SKUS.map((sku, index) => (
                        <React.Fragment key={sku.skuId}>
                            {renderTopCell({
                                sku,
                                index,
                                isSkeleton: true
                            })}
                        </React.Fragment>
                    ))}
                </Grid>
            ) : recommendedSkus?.length ? (
                <Box
                    ref={scrollRef}
                    css={{
                        overflowX: 'auto',
                        width: '100%',
                        scrollBehavior: 'smooth',
                        scrollbarWidth: 'none',
                        overscrollBehaviorX: 'none',
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}
                >
                    <Grid
                        columns={gridConfig.columns}
                        fontSize={['sm', 'base']}
                        gap={null}
                        css={{
                            minWidth: gridConfig.columnCount < 6 ? `${gridConfig.widthPx}px` : '100%',
                            scrollSnapType: 'x mandatory',
                            [`& > *:nth-of-type(-n + ${gridConfig.columnCount})`]: {
                                scrollSnapAlign: 'start'
                            }
                        }}
                        data-cnstrc-recommendations
                        data-cnstrc-recommendations-pod-id={podId}
                        data-cnstrc-result-id={resultId}
                        data-cnstrc-num-results={totalResults}
                    >
                        {/* Row 1: Top cells (product images, brand, name) */}
                        {recommendedSkus.map((sku, index) => (
                            <React.Fragment key={sku?.skuId || index}>
                                {renderTopCell({
                                    sku,
                                    index,
                                    podId,
                                    isCarousel
                                })}
                            </React.Fragment>
                        ))}

                        {/* Always-visible attributes (price, rating) */}
                        {filteredPdpCompareAttributes
                            .filter(attr => attr === 'price' || attr === 'rating')
                            .map(attribute => {
                                const attributeKey = typeof attribute === 'string' ? attribute : JSON.stringify(attribute);

                                if (attribute === 'price') {
                                    return recommendedSkus.map((sku, index) => (
                                        <React.Fragment key={`${attributeKey}-${sku?.skuId || index}`}>
                                            {renderPriceCell({
                                                sku,
                                                index
                                            })}
                                        </React.Fragment>
                                    ));
                                }

                                if (attribute === 'rating') {
                                    const anySkuHasReviews = recommendedSkus.some(sku => sku?.productReviewCount > 0);

                                    if (!anySkuHasReviews) {
                                        return null;
                                    }

                                    return recommendedSkus.map((sku, index) => (
                                        <React.Fragment key={`${attributeKey}-${sku?.skuId || index}`}>
                                            {renderRatingCell({
                                                sku,
                                                index
                                            })}
                                        </React.Fragment>
                                    ));
                                }

                                return null;
                            })}

                        {/* Collapsible attributes (configured attributes) */}
                        {(() => {
                            const collapsibleAttributes = filteredPdpCompareAttributes.filter(attr => attr !== 'price' && attr !== 'rating');

                            if (collapsibleAttributes.length === 0) {
                                return null;
                            }

                            const attributeCells = collapsibleAttributes
                                .map((attribute, attrIndex) => {
                                    const attributeKey = typeof attribute === 'string' ? attribute : JSON.stringify(attribute);

                                    const cellResults = recommendedSkus.map((sku, index) =>
                                        renderAttributeCell({
                                            sku,
                                            index,
                                            attribute,
                                            attrIndex
                                        })
                                    );

                                    const allCellsAreEmpty = cellResults?.every(result => result?.isCellEmpty);

                                    if (allCellsAreEmpty) {
                                        return null;
                                    }

                                    return cellResults.map((result, index) => (
                                        <React.Fragment key={`${attributeKey}-${recommendedSkus[index]?.skuId || index}`}>
                                            {result.cell}
                                        </React.Fragment>
                                    ));
                                })
                                .filter(Boolean);

                            if (attributeCells.length === 0) {
                                return null;
                            }

                            if (!isCollapsible) {
                                return attributeCells;
                            }

                            return <div css={[styles.collapsibleWrapper, !isExpanded && styles.collapsibleCollapsed]}>{attributeCells}</div>;
                        })()}
                    </Grid>
                    {isCollapsible && hasCollapsibleAttributes && (
                        <Text
                            is='button'
                            color='blue'
                            fontSize='base'
                            marginTop={3}
                            onClick={toggleExpanded}
                            css={styles.toggleButton}
                        >
                            {getText(isExpanded ? 'showLess' : 'showMore')}
                        </Text>
                    )}
                </Box>
            ) : (
                <Text css={styles.noResults}>{getText('noProducts')}</Text>
            )}
        </Box>
    );
};

const styles = {
    noResults: {
        marginTop: space[4],
        display: 'inline-block',
        paddingLeft: space[1]
    },
    collapsibleWrapper: {
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'inherit',
        position: 'relative'
    },
    collapsibleCollapsed: {
        maxHeight: COLLAPSED_MAX_HEIGHT,
        overflow: 'hidden',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: GRADIENT_OVERLAY_HEIGHT,
            background: `linear-gradient(to bottom, transparent, ${colors.white})`,
            pointerEvents: 'none'
        }
    },
    toggleButton: {
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        position: 'sticky',
        left: 0
    }
};

export default wrapFunctionalComponent(ProductCompareTool, 'ProductCompareTool');
